import { i18n } from "~vue/i18n";
import { Duration } from "luxon";

export type TutorialCategory = "getting_started" | "everyday_coaching" | "practice_and_prepare" | "growth_and_feedback" | "where_you_work";
export type VideoCategory = TutorialCategory | "start_here";

export interface TutorialVideo {
    id: string;
    title: string;
    category: VideoCategory;
    durationSeconds: number;
    file: string;
    poster: string;
    captions: string;
}

export interface WalkthroughVideo extends TutorialVideo {
    src: string;
}

export const TUTORIAL_CATEGORIES: { id: TutorialCategory; title: string }[] = [
    {
        id: "getting_started",
        get title() {
            return i18n.global.t("chat.tutorial.categories.gettingStarted");
        },
    },
    {
        id: "everyday_coaching",
        get title() {
            return i18n.global.t("chat.tutorial.categories.everydayCoaching");
        },
    },
    {
        id: "practice_and_prepare",
        get title() {
            return i18n.global.t("chat.tutorial.categories.practiceAndPrepare");
        },
    },
    {
        id: "growth_and_feedback",
        get title() {
            return i18n.global.t("chat.tutorial.categories.growthAndFeedback");
        },
    },
    {
        id: "where_you_work",
        get title() {
            return i18n.global.t("chat.tutorial.categories.whereYouWork");
        },
    },
];

const posterAssets = import.meta.glob<string>("../../../assets/nadia-tutorial/*.v1-poster.webp", {
    eager: true,
    import: "default",
    query: "?url",
});
const captionAssets = import.meta.glob<string>("../../../assets/nadia-tutorial/*.v1.en.vtt", {
    eager: true,
    import: "default",
    query: "?url",
});

function assetFor(assets: Record<string, string>, file: string): string {
    return Object.entries(assets).find(([path]) => path.endsWith(file))?.[1] ?? "";
}

function video(id: string, title: () => string, category: VideoCategory, durationSeconds: number, slug: string): TutorialVideo {
    return {
        id,
        get title() {
            return title();
        },
        category,
        durationSeconds,
        file: `${slug}.v1.mp4`,
        poster: assetFor(posterAssets, `${slug}.v1-poster.webp`),
        captions: assetFor(captionAssets, `${slug}.v1.en.vtt`),
    };
}

export const introVideo = video("learn_nadia_intro", () => i18n.global.t("chat.tutorial.videos.learnNadiaIntro"), "start_here", 66, "learn-nadia-in-under-2-mins");

export const walkthroughVideos: TutorialVideo[] = [
    video("ask_nadia_anything", () => i18n.global.t("chat.tutorial.videos.askNadiaAnything"), "getting_started", 33, "ask-nadia-anything"),
    video("help_nadia_get_to_know_you", () => i18n.global.t("chat.tutorial.videos.helpNadiaGetToKnowYou"), "getting_started", 34, "help-nadia-get-to-know-you"),
    video("choose_what_you_share", () => i18n.global.t("chat.tutorial.videos.chooseWhatYouShare"), "getting_started", 42, "choose-what-you-share"),
    video("pick_up_where_you_left_off", () => i18n.global.t("chat.tutorial.videos.pickUpWhereYouLeftOff"), "getting_started", 36, "pick-up-where-you-left-off"),
    video("prepare_for_a_hard_conversation", () => i18n.global.t("chat.tutorial.videos.prepareForAHardConversation"), "everyday_coaching", 56, "prepare-for-a-hard-conversation"),
    video("prepare_for_a_meeting", () => i18n.global.t("chat.tutorial.videos.prepareForAMeeting"), "everyday_coaching", 38, "prepare-for-a-meeting"),
    video("see_what_nadia_noticed_this_week", () => i18n.global.t("chat.tutorial.videos.seeWhatNadiaNoticedThisWeek"), "everyday_coaching", 22, "see-what-nadia-noticed-this-week"),
    video("turn_a_tip_into_a_plan", () => i18n.global.t("chat.tutorial.videos.turnATipIntoAPlan"), "everyday_coaching", 49, "turn-a-tip-into-a-plan"),
    video("work_from_a_document", () => i18n.global.t("chat.tutorial.videos.workFromADocument"), "everyday_coaching", 33, "work-from-a-document"),
    video("practice_a_conversation", () => i18n.global.t("chat.tutorial.videos.practiceAConversation"), "practice_and_prepare", 71, "practice-a-conversation"),
    video("tell_nadia_about_your_people", () => i18n.global.t("chat.tutorial.videos.tellNadiaAboutYourPeople"), "practice_and_prepare", 39, "tell-nadia-about-your-people"),
    video(
        "help_nadia_get_to_know_your_teammates",
        () => i18n.global.t("chat.tutorial.videos.helpNadiaGetToKnowYourTeammates"),
        "practice_and_prepare",
        16,
        "help-nadia-get-to-know-your-teammates",
    ),
    video("set_and_track_a_goal", () => i18n.global.t("chat.tutorial.videos.setAndTrackAGoal"), "growth_and_feedback", 26, "set-and-track-a-goal"),
    video(
        "get_feedback_from_colleagues_360",
        () => i18n.global.t("chat.tutorial.videos.getFeedbackFromColleagues360"),
        "growth_and_feedback",
        62,
        "get-feedback-from-colleagues-360",
    ),
    video("get_ready_for_your_review", () => i18n.global.t("chat.tutorial.videos.getReadyForYourReview"), "growth_and_feedback", 25, "get-ready-for-your-review"),
    video("nadia_in_teams", () => i18n.global.t("chat.tutorial.videos.nadiaInTeams"), "where_you_work", 46, "nadia-in-teams"),
    video("insights_in_slack", () => i18n.global.t("chat.tutorial.videos.insightsInSlack"), "where_you_work", 40, "insights-in-slack"),
];

export function resolveVideo(video: TutorialVideo, baseUrl: string): WalkthroughVideo {
    return { ...video, src: `${baseUrl.replace(/\/+$/, "")}/${video.file}` };
}

export function formatDuration(seconds: number): string {
    return Duration.fromObject({ seconds }).toFormat("m:ss");
}
